import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Volume2, VolumeX } from "lucide-react";

const VIDEOS = [
  { src: "/media/video-1.mp4", poster: "/media/video-1-poster.jpg", name: "Powerbox G2", slug: "power-box-g2", desc: "Slimline, wall-mountable and easy to install." },
  { src: "/media/video-2.mp4", poster: "/media/video-2-poster.jpg", name: "Cygni All in One", slug: "cygni-all-in-one", desc: "All-in-one battery and hybrid inverter with 11 international awards." },
  { src: "/media/video-3.mp4", poster: "/media/video-3-poster.jpg", name: "Stack100 Pro", slug: "power-stack-pro-100", desc: "Modular and stackable — scale your storage as needs grow." },
];

const SPARKLES = [
  { top: "-2%", left: "8%", s: 10, d: "0s" },
  { top: "9%", left: "-3%", s: 8, d: ".8s" },
  { top: "-3%", left: "68%", s: 12, d: ".4s" },
  { top: "5%", left: "96%", s: 9, d: "1.2s" },
  { top: "40%", left: "-4%", s: 7, d: "1.6s" },
  { top: "55%", left: "99%", s: 11, d: ".2s" },
  { top: "88%", left: "4%", s: 9, d: "1s" },
  { top: "94%", left: "80%", s: 12, d: ".6s" },
  { top: "78%", left: "50%", s: 7, d: "1.4s" },
];

export default function VideoGallery({ embedded = false }) {
  const [active, setActive] = useState(0);
  const [musicOn, setMusicOn] = useState(false);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const current = VIDEOS[active];

  // Autoplay each clip as it becomes active (always muted; music is separate).
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }, [active]);

  const toggleMusic = () => {
    const a = audioRef.current;
    if (!a) return;
    if (musicOn) {
      a.pause();
      setMusicOn(false);
    } else {
      const p = a.play();
      if (p && p.then) p.then(() => setMusicOn(true)).catch(() => {});
      else setMusicOn(true);
    }
  };

  const next = () => setActive((i) => (i + 1) % VIDEOS.length);

  return (
    <section className={`relative overflow-hidden ${embedded ? "w-full" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24"}`} id="videos" data-testid="video-gallery">
      <style>{`
        @keyframes eiShineSweep {0%{transform:translateX(-130%) skewX(-18deg);opacity:0}15%{opacity:.6}55%{opacity:0}100%{transform:translateX(240%) skewX(-18deg);opacity:0}}
        @keyframes eiSparkle {0%,100%{opacity:0;transform:scale(.4) rotate(0deg)}50%{opacity:1;transform:scale(1) rotate(45deg)}}
        @keyframes eiGlow {0%,100%{opacity:.3;transform:scale(1)}50%{opacity:.6;transform:scale(1.07)}}
        .ei-vg-glow{animation:eiGlow 4s ease-in-out infinite}
        .ei-vg-sparkle{position:absolute;border-radius:9999px;background:radial-gradient(circle,#fff 0%,#E7C558 45%,transparent 72%);box-shadow:0 0 8px 2px rgba(231,197,88,.7);animation:eiSparkle 2.6s ease-in-out infinite;pointer-events:none;z-index:20}
        .ei-vg-shine{position:absolute;inset:0;overflow:hidden;pointer-events:none;z-index:10}
        .ei-vg-shine::before{content:'';position:absolute;top:-10%;left:0;height:120%;width:35%;background:linear-gradient(100deg,transparent,rgba(255,255,255,.4),rgba(231,197,88,.25),transparent);filter:blur(2px);animation:eiShineSweep 5s ease-in-out infinite}
      `}</style>

      <div className={`relative mt-2 mx-auto ${embedded ? "max-w-xl lg:max-w-none lg:mx-0" : "max-w-md lg:max-w-xl"}`}>
        {/* gold glow */}
        <div
          className="ei-vg-glow absolute -inset-8 rounded-[40px] blur-2xl pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(212,175,55,0.40), transparent 70%)" }}
          aria-hidden="true"
        />

        {/* glitter */}
        {SPARKLES.map((sp) => (
          <span
            key={`${sp.top}-${sp.left}`}
            className="ei-vg-sparkle"
            style={{ top: sp.top, left: sp.left, width: sp.s, height: sp.s, animationDelay: sp.d }}
            aria-hidden="true"
          />
        ))}

        <div className="relative rounded-lg overflow-hidden border border-ei-mist shadow-xl bg-black">
          <video
            ref={videoRef}
            key={current.src}
            data-testid="gallery-video"
            className="w-full h-auto block max-h-[80vh] mx-auto"
            autoPlay
            muted
            playsInline
            preload="auto"
            onEnded={next}
            poster={current.poster}
          >
            <source src={current.src} type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          {/* shine sweep */}
          <div className="ei-vg-shine" aria-hidden="true" />

          <audio ref={audioRef} src="/media/showcase-music.mp3" loop preload="none" />

          <button
            onClick={toggleMusic}
            data-testid="gallery-music-toggle"
            aria-label={musicOn ? "Turn music off" : "Turn music on"}
            className="absolute bottom-3 right-3 z-20 w-10 h-10 rounded-full bg-ei-navy/70 text-white flex items-center justify-center hover:bg-ei-navy transition-colors"
          >
            {musicOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
        </div>

        {/* progress / chapter selectors */}
        <div className="mt-4 flex items-center justify-center gap-2" data-testid="gallery-progress">
          {VIDEOS.map((v, i) => (
            <button
              key={v.src}
              onClick={() => setActive(i)}
              data-testid={`gallery-thumb-${i}`}
              aria-label={`Play ${v.name}`}
              className={`h-2.5 rounded-full transition-all ${
                i === active ? "w-8 bg-ei-gold" : "w-2.5 bg-ei-mist hover:bg-ei-graphite/40"
              }`}
            />
          ))}
        </div>
      </div>

      <div className="mt-8 text-center mx-auto max-w-2xl">
        <div className="ei-eyebrow text-ei-golddark">Product showcase</div>
        <h2
          key={current.name}
          data-testid="gallery-active-title"
          className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ei-navy ei-rise"
        >
          {current.name}
        </h2>
        <p key={current.desc} className="mt-3 text-base text-ei-graphite/80 ei-rise" data-testid="gallery-active-desc">{current.desc}</p>
        <Link
          to={`/products/${current.slug}`}
          data-testid="gallery-active-link"
          className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-ei-golddark hover:text-ei-navy"
        >
          View product <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}
